import _ from 'lodash';
import memoize from 'memoize-weak';

import NodeUtils from 'app/beta-editor/utils/NodeUtils';
import { getZapState } from 'app/beta-editor/utils/FlowUtils';
import { matchesQuery } from 'app/common/CommonUtils';
import {
  HELD_FOLDER_ID,
  HOME_FOLDER_ID,
  TRASH_FOLDER_ID,
  homeFolder,
} from 'app/dashboard/utils/constants';

export const matchesSearchCriteria = (zap, services, search) => {
  if (!search) {
    // no search, we're in!
    return true;
  }

  if (matchesQuery(search, zap.title || NodeUtils.EMPTY_ZAP_NAME)) {
    return true;
  }

  if (zap.nodes) {
    return _.some(zap.nodeIds, nodeId => {
      const node = zap.nodes[nodeId];
      const selectedApi = node.selected_api;
      const apiService = services[selectedApi];
      if (!apiService) {
        return false;
      }
      return matchesQuery(search, apiService.name);
    });
  }
  return false;
};

const isFilterPassingByKey = ({ key, value }, zap) => {
  switch (key) {
    case 'state':
      if (value === 'all') {
        return true;
      } else if (value === '-draft' && zap.state !== 'draft') {
        return true;
      } else {
        return value === zap.state;
      }
    case 'is_shared':
      return value === zap.is_shared;
    case 'owner_id':
      return value === zap.owner_id;
  }
  return false;
};

export const filterZaps = memoize(
  (zaps, services, search, filterBy, folderId, zapIdsWithHeldTasks = []) => {
    /**
     * For the filter predicate function, make sure to return early false only since we want to fall through ALL the way to the end.
     * We need to fall through to the end for the search (no folderId) to work using the filterBy options.
     */
    const zapsToDisplay = _.filter(zaps, zap => {
      if (!matchesSearchCriteria(zap, services, search)) {
        return false;
      }

      // Home folder gets special treatment.
      if (folderId === HOME_FOLDER_ID) {
        // Show zaps with no folders in Home
        if (zap.folder_ids && zap.folder_ids.length > 0) {
          // Or zaps that only are only/explicitly in the Home folder.
          // Not necessary right now, but covering just in case.
          if (
            zap.folder_ids.length > 1 ||
            zap.folder_ids[0] !== HOME_FOLDER_ID
          ) {
            return false;
          }
        }
      } else if (folderId === HELD_FOLDER_ID) {
        const doesZapBelongInHeldFolder = _.includes(
          zapIdsWithHeldTasks,
          zap.id
        );

        if (!doesZapBelongInHeldFolder) {
          return false;
        }
        // Otherwise, only show zaps which belong to the folder.
      } else if (
        folderId &&
        zap.folder_ids &&
        !_.includes(zap.folder_ids, folderId)
      ) {
        return false;
      } else if (
        typeof folderId === 'undefined' &&
        _.includes(zap.folder_ids, TRASH_FOLDER_ID)
      ) {
        return false;
      }

      if (typeof filterBy === 'object') {
        return Object.keys(filterBy).every(filter =>
          isFilterPassingByKey({ key: filter, value: filterBy[filter] }, zap)
        );
      }

      // When there is no filterBy defined
      return true;
    });

    return zapsToDisplay;
  }
);

export const orderZaps = zaps => {
  // sort the zaps in *ascending* order by zap title
  zaps = _.sortBy(zaps, zap => {
    return (zap.title || '').toLowerCase();
  });

  return zaps;
};

const stateValues = {
  on: 1,
  off: 2,
  draft: 3,
};

export const orderZapsByState = zaps => {
  // sort the zaps in on/off/draft state order
  zaps = _.sortBy(zaps, zap => {
    const zapState = getZapState(zap);
    return stateValues[zapState] || 9999;
  });

  return zaps;
};

export const groupByState = zaps => {
  return _.groupBy(zaps, 'state');
};

export const groupByFolder = zaps => {
  return _.groupBy(zaps, zap => {
    if (!zap.folder_ids || !zap.folder_ids.length) {
      return 0;
    }
    return zap.folder_ids[0];
  });
};

const pluckFolderIds = folders => {
  return _.map(folders, 'id');
};

export const getKeysToIntArray = obj => {
  return _.map(_.keys(obj), id =>
    isNaN(parseInt(id, 10)) ? id : parseInt(id, 10)
  );
};

export const orderFolderIdsByTitle = (folderIds, folders) => {
  return _.sortBy(folderIds, folderId => {
    const folder = folders[folderId];
    if (!folder) {
      return '';
    }

    // 'Cuz we want trash folder to show up last when we enable search for Trash folder
    if (folder.id === TRASH_FOLDER_ID) {
      return 'zzzzz';
    }

    return (folder.title || '').toLowerCase();
  });
};

const countZapsInFolderBase = (predicate, folderId, zaps) =>
  _.reduce(zaps, (memo, zap) => memo + (predicate(folderId, zap) ? 1 : 0), 0);

const getZapFolders = zap => _.get(zap, 'folder_ids', []);

const isZapFolderless = zap => _.isEmpty(getZapFolders(zap));

export const isZapInFolder = (folderId, zap) => {
  return folderId === HOME_FOLDER_ID
    ? isZapFolderless(zap)
    : _.includes(getZapFolders(zap), folderId);
};

export const isZapInTrash = _.partial(isZapInFolder, TRASH_FOLDER_ID);

export const zapsInFolder = (folderId, zaps) => {
  return _.filter(zaps, zap => isZapInFolder(folderId, zap));
};

export const zapIdsInFolder = (folderId, zaps) => {
  return zapsInFolder(folderId, zaps).map(zap => zap.id);
};

export const countZapsInFolder = _.partial(
  countZapsInFolderBase,
  isZapInFolder
);

export const countZapsInHomeFolder = _.partial(
  countZapsInFolderBase,
  (folderId, zap) => isZapFolderless(zap),
  0
);

export const countZapsInTrashFolder = _.partial(
  countZapsInFolderBase,
  isZapInFolder,
  TRASH_FOLDER_ID
);

export const sortFoldersByTitleWithHome = folders => {
  const folderIds = pluckFolderIds(folders);
  return orderFolderIdsByTitle(folderIds, folders)
    .filter(id => !folders[id].isTrash)
    .map(id => folders[id])
    .reverse()
    .concat(homeFolder)
    .reverse();
};

export const canMoveZapToFolder = ({ isOwnerOfZap }, folder) => {
  return folder.isShared || isOwnerOfZap;
};

export const propifyAuth = auth => ({
  id: auth.id,
  selectedApi: auth.selected_api,
  title: auth.title,
});

export const getOrderedFolderIdsByTitle = (zaps, folders) => {
  const zapsByFolder = groupByFolder(zaps);
  const folderIds = getKeysToIntArray(zapsByFolder);

  return orderFolderIdsByTitle(folderIds, folders);
};

export const RUN_ZAP_STATUS_ICON_MAP = {
  success: 'check',
  halted: 'error',
  error: 'error',
  held: 'error',
};

export const getDashboardRoute = ({ folderId }) => {
  if (!folderId) {
    return '/app/zaps';
  }
  return `/app/zaps/folder/${folderId}`;
};

export const isInTrash = folderId => {
  return folderId === TRASH_FOLDER_ID;
};

export const isInHeld = folderId => {
  return folderId === HELD_FOLDER_ID;
};

export const getZapsGroupedByFolder = (zaps, folders) => {
  if (!zaps) {
    return {};
  }

  const zapsByFolder = groupByFolder(zaps);
  const orderedFolderIds = getOrderedFolderIdsByTitle(zaps, folders);

  return {
    zapsByFolder,
    orderedFolderIds,
  };
};

export const sortZapsByCopiedOrder = (zaps, copiedZaps) => {
  const totalClonedZapIds = _.flatten(_.values(copiedZaps));
  const zapsWithoutClones = zaps.filter(z => !totalClonedZapIds.includes(z.id));

  return zapsWithoutClones.reduce((acc, zap) => {
    const { id } = zap;
    const clonedZapIds = copiedZaps[id] || [];
    const clonedZapsInOrder = clonedZapIds.map(zapId => {
      return _.find(zaps, z => z.id === zapId);
    });
    return _.compact(acc.concat(zap).concat(clonedZapsInOrder));
  }, []);
};
